import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { attachTilt } from '../../../hooks/useTiltCards';

gsap.registerPlugin(ScrollTrigger);

const pillars = [
  {
    tag: '01 · Investing',
    title: 'Capital that moves first.',
    description: 'Pre-seed and seed checks into deeply technical founders solving frontier problems. We lead, follow, and write conviction-sized checks.',
    bullets: ['$250K — $3M checks', 'Pre-seed · Seed · Series A', 'Lead or co-lead'],
    to: '/investing',
    image: 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20visualization%20of%20a%20glowing%20golden%20cube%20suspended%20in%20a%20dark%20studio%20environment%20surrounded%20by%20delicate%20particle%20mist%2C%20cinematic%20still%20life%20with%20warm%20amber%20rim%20lighting%20on%20deep%20charcoal%20background%2C%20premium%20editorial%20technology%20composition&width=900&height=1100&seq=vex-pillar-01&orientation=portrait',
  },
  {
    tag: '02 · Building',
    title: 'A studio for the unbuilt.',
    description: 'Atlas Labs is our in-house venture studio. We co-found, prototype, and incubate companies alongside exceptional operators.',
    bullets: ['Studio model', 'Founder-in-residence', 'Shared resources & playbook'],
    to: '/building',
    image: 'https://readdy.ai/api/search-image?query=Minimalist%20industrial%20still%20life%20with%20stacked%20matte%20black%20geometric%20blocks%20arranged%20on%20a%20dark%20concrete%20surface%20with%20soft%20warm%20directional%20studio%20light%2C%20cinematic%20editorial%20composition%2C%20negative%20space%2C%20premium%20engineering%20aesthetic&width=900&height=1100&seq=vex-pillar-02&orientation=portrait',
  },
  {
    tag: '03 · Advisory',
    title: 'Operators on call.',
    description: 'Strategic guidance, hiring, fundraising prep, and product reviews. Our partners stay close to every company we back.',
    bullets: ['Hands-on partners', 'Network access', 'Quarterly diligence'],
    to: '/advisory',
    image: 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20still%20life%20of%20a%20vintage%20brass%20compass%20resting%20on%20a%20dark%20linen%20surface%20with%20soft%20warm%20amber%20directional%20studio%20light%2C%20cinematic%20composition%20with%20deep%20negative%20space%2C%20premium%20editorial%20technology%20aesthetic&width=900&height=1100&seq=vex-pillar-03&orientation=portrait',
  },
];

export default function PillarShowcase() {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      const cards = el.querySelectorAll<HTMLElement>('.pillar-card');
      cards.forEach((card) => {
        gsap.fromTo(
          card,
          { y: 60, opacity: 0, filter: 'blur(16px)' },
          {
            y: 0,
            opacity: 1,
            filter: 'blur(0px)',
            duration: 1.6,
            ease: 'power3.out',
            scrollTrigger: { trigger: card, start: 'top 88%' },
          }
        );

        const img = card.querySelector<HTMLElement>('.pillar-image');
        if (img) {
          gsap.fromTo(
            img,
            { yPercent: -8 },
            {
              yPercent: 8,
              ease: 'none',
              scrollTrigger: {
                trigger: card,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true,
              },
            }
          );
        }
      });
    }, el);
    const cleanupTilt = attachTilt(el, 4);
    return () => {
      ctx.revert();
      cleanupTilt();
    };
  }, []);

  return (
    <section ref={ref} className="relative bg-[#050505] text-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {pillars.map((p) => (
            <Link
              key={p.tag}
              to={p.to}
              className="pillar-card tilt-card hover-glow group relative rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 transition-colors min-h-[520px] flex flex-col justify-end"
            >
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title}
                  className="pillar-image absolute inset-0 w-full h-[120%] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/10" />
              </div>
              <div className="relative p-7 md:p-8 tilt-lift">
                <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/50 mb-4">
                  {p.tag}
                </div>
                <h3 className="font-display text-3xl md:text-4xl mb-4 leading-tight">
                  <span className="title-shimmer inline-block">{p.title}</span>
                </h3>
                <p className="text-white/65 text-sm leading-relaxed mb-6">{p.description}</p>
                <ul className="space-y-2 mb-7">
                  {p.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/50"
                    >
                      <span className="w-3 h-px bg-white/40" />
                      {b}
                    </li>
                  ))}
                </ul>
                <span className="inline-flex items-center gap-2 text-sm font-medium text-white group-hover:gap-3 transition-all">
                  Learn more
                  <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}